import { styled } from '@mui/material';
import Icon from 'Components/_NewElements/Icon';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import { Container, Item } from 'Components/Grid';
import { DateTime } from 'luxon';
import { partialDateFromDate } from 'neuro-utils';
import * as React from 'react';
import { formatPartialDate } from 'Utility/date';
import colors from '../../../config/theme/colors';

import type { IActionButtonRounded } from 'Components/ActionButtonRounded';
import type { TTOPIC } from 'Store/messaging/types/TTopic';

const StyledFilterHeader = styled('div')({
  fontSize: '1.6rem',
  lineHeight: '1.8rem',
  fontWeight: '600',
  marginRight: '3rem',
});

const StyledInfoFieldsArea = styled('div')(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '1.4rem',
  //padding: '0 2rem',
  //textAlign: 'center'
}));

export const FilterField = ({
  title,
  type,
  children,
}: {
  title: string;
  type: 'row' | 'col';
  children: React.JSX.Element | string | React.JSX.Element[];
}) => {
  return (
    <Container rowGap={'0.5rem'} alignItems={'center'}>
      <Item xs={type === 'row' ? 'auto' : 12}>
        <StyledFilterHeader style={{ textAlign: type === 'row' ? 'end' : 'start' }}>{title}</StyledFilterHeader>
      </Item>
      <Item xs={true}>{children}</Item>
    </Container>
  );
};

export const MessageLayout = ({ children, infoFields, buttons }: IMessageLayout) => {
  const buttonsProps: Partial<IActionButtonRounded> = {
    width: 22,
    height: 3,
    fontSize: 15,
    uppercase: false,
  };
  return (
    <Container>
      <Item xs={7.5} md={8.5}>
        {/* Main message area */}
        {children}
      </Item>
      <Item xs={0.5} />
      <Item xs={4} md={3}>
        <Container justifyContent="center">
          <Item>
            {/* Info fields */}
            {infoFields && (
              <StyledInfoFieldsArea>
                {infoFields.map((info, i) => (
                  <div key={i}>{info}</div>
                ))}
              </StyledInfoFieldsArea>
            )}
            {infoFields && <div style={{ marginTop: '2rem' }} />}
            {/* Buttons area */}
            {buttons.map((but, i) => (
              <div key={i} style={{ marginBottom: '1rem' }}>
                <ActionButtonRounded key={i} {...buttonsProps} {...but} />
              </div>
            ))}
          </Item>
        </Container>
      </Item>
    </Container>
  );
};

interface IMessageLayout {
  children: React.JSX.Element;
  infoFields?: React.JSX.Element[];
  buttons: IActionButtonRounded[];
}

export const MessageDetailListItem = styled(Container)(({ theme }) => ({
  paddingLeft: '1rem',
  paddingTop: '0.5rem',
  borderTop: `0.1rem solid ${theme.palette.grey[500]}`,

  '* > &:not(:last-of-type)': {
    paddingBottom: '0.5rem',
  },
}));

export const MessageDetailListTitle = styled(Item)(({ theme }) => ({
  color: theme.palette.grey[700],
}));

export const MessageDetailRow = ({
  title,
  value,
  titleWidth = 4,
  fullWidthValue,
}: {
  title?: string | React.JSX.Element;
  value?: string | React.JSX.Element | null;
  titleWidth?: number;
  fullWidthValue?: boolean;
}) => (
  <MessageDetailListItem>
    {title && <MessageDetailListTitle xs={titleWidth}>{title}</MessageDetailListTitle>}
    <Item style={{ whiteSpace: 'pre-line', fontWeight: 600 }} xs={fullWidthValue ? 12 : true}>
      {value ?? '-'}
    </Item>
  </MessageDetailListItem>
);

export const timeFromDate = (date: Date) => {
  const minutes = `${date.getMinutes().toString().length === 1 ? 0 : ''}${date.getMinutes()}`;

  return `${date.getHours()}:${minutes}`;
};

export const HeaderTimeDisplay = ({ date, color }: { date: number; color?: 'gray' | 'yellow' }) => {
  const jsDate = new Date(date);

  const dateTime = DateTime.fromJSDate(jsDate);

  return (
    <div style={{ fontSize: '1.4rem', fontWeight: 600, marginTop: '0', display: 'flex', alignItems: 'center' }}>
      <Icon
        icon={'event'}
        size={1.8}
        color={color == 'gray' ? 'grayDark' : color == 'yellow' ? 'warningDark' : undefined}
      />
      <div style={{ marginLeft: '0.5rem', marginRight: '2rem' }}>{formatPartialDate(partialDateFromDate(jsDate))}</div>
      <Icon
        icon={'time'}
        size={1.8}
        color={color == 'gray' ? 'grayDark' : color == 'yellow' ? 'warningDark' : undefined}
      />
      <div style={{ marginLeft: '0.5rem' }}>{timeFromDate(jsDate)}</div>
      <div style={{ marginLeft: '2rem', fontWeight: 400 }}>{`(${dateTime.toRelative()})`}</div>
    </div>
  );
};

export const hasPatientData = (
  targetPatients: TTOPIC['targetPatients'],
): targetPatients is { lastNames: string; firstNames: string; id: string }[] => {
  if (typeof targetPatients[0] === 'string') return false;
  return true;
};

export const openPDFOnclick = (thisAttachmentData: { type: string; blob: Blob }) => {
  if (thisAttachmentData.type === 'pdf') {
    const file = new Blob([thisAttachmentData.blob], {
      type: 'application/pdf',
    });

    const fileURL = URL.createObjectURL(file);
    window.open(fileURL);
  }
};

export type TAttachmentData = { type: 'image' | 'pdf'; blob: Blob };

export const isThread = (item: IThreadListItem | TTOPIC): item is IThreadListItem => {
  if ('topicId' in item) return true;
  return false;
};

export const ItemStatus = ({ item, fm }: IItemStatus) => {
  const status = !item.assignedFor
    ? 'unassigned'
    : isThread(item) && item.latestMessage?.application === 'myneuro'
      ? 'waiting'
      : isThread(item)
        ? 'idle'
        : 'groupMessage';

  const statusIcon =
    status === 'unassigned'
      ? 'alert'
      : status === 'waiting'
        ? 'question'
        : status === 'groupMessage'
          ? 'group'
          : 'success';

  const ended = isThread(item)
    ? item.completionTimestamp && item.completionTimestamp >= (item.latestMessage?.timestamp ?? 0)
    : item.closureTimestamp && Date.now() >= item.closureTimestamp;

  return (
    <Container
      alignItems="center"
      style={{
        fontSize: '1.4rem',
        fontWeight: status === 'unassigned' ? 600 : status === 'waiting' ? 600 : 400,
        color: ended
          ? colors.darkGray
          : status === 'unassigned'
            ? colors.error.default
            : status === 'waiting'
              ? colors.warning.default
              : undefined,
      }}
    >
      <Item style={{ padding: '0 0.5rem 0 0' }}>
        <Icon
          icon={statusIcon}
          size={1.8}
          color={
            ended
              ? 'grayDark'
              : status === 'unassigned'
                ? 'errorDefault'
                : status === 'waiting'
                  ? 'warningDefault'
                  : status === 'groupMessage'
                    ? 'warningDark'
                    : undefined
          }
        />
      </Item>
      <Item>
        {fm('messaging.statusOpt.' + (status === 'groupMessage' ? 'groupMessage' : ended ? 'markEnded' : status))}
      </Item>
    </Container>
  );
};

interface IItemStatus {
  item: IThreadListItem | TTOPIC;
  fm: (id: string) => string;
}
